var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { bind } from 'bind-decorator';
import { computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Subscription } from '@lean-body/src/util/index';
import { take } from 'rxjs/operators';
import { LessonResultSectionVM } from '@lean-body/components/organisms/lesson_result_section';
import { VideoElementEventHandler, VideoPlayAnalyzer, VideoPlayerContext, VideoPlayerStatus, VideoState, } from '@lean-body/src/domain';
import { LBPlayer } from '../lb_player';
import { VideoType } from '@lean-body/src/domain/video';
import { VjsCompleteButtonVM } from '../lb_player/components/VjsCompleteButton';
import { LBPlayerContainer } from '../organisms/lb_player_container';
export const Video = observer(({ vm, src, playButtonVisible, onClickPlayButton }) => {
    const [videoEl, setVideoEl] = React.useState(null);
    const [lbPlayerCtx, setLbPlayerCtx] = React.useState(null);
    React.useEffect(() => {
        if (videoEl && lbPlayerCtx) {
            vm.initializeVideoElement(videoEl, lbPlayerCtx);
        }
    }, [vm, videoEl, lbPlayerCtx]);
    if (vm === null) {
        return;
    }
    const onClick = () => {
        vm.setIsPaused(false);
        onClickPlayButton();
    };
    const videoOperationProps = {
        apiClient: vm.apiClient,
        onClickExitInnerFullScreen: vm.onClickExitInnerFullScreen,
        onClickPrev: vm.onClickPrev,
        onClickNext: vm.onClickNext,
        isFirstLesson: vm.isFirstLesson,
        isLastLesson: vm.isLastLesson,
        // 自動再生無効時は自動再生トグルを非表示にする
        showAutoplayToggle: !vm.hideAutoPlay,
        showBackButton: true,
        isAlwaysShowStepButtons: false,
        completeButtonVM: vm.completeButtonVM,
        onClickComplete: vm.onLessonComplete,
    };
    const resultOptions = {
        isVisible: vm.resultSectionVisible,
        vm: vm.resultVM,
        addFavoriteLesson: vm.apiClient.addFavoriteLesson,
        unFavoriteLesson: vm.apiClient.unFavoriteLesson,
    };
    const lbVideoOptions = (src) => {
        return {
            sources: [
                {
                    src,
                    type: 'application/x-mpegURL',
                },
            ],
            preload: 'auto',
            playsinline: true, // iPhoneで自動的にiPhoneの動画再生プレイヤーが立ち上がってしまうのを回避するため
            controls: true,
            controlBar: {
                playToggle: false,
                volumePanel: false,
                muteToggle: false,
                fullscreenToggle: false,
                pictureInPictureToggle: false,
            },
        };
    };
    return (React.createElement(LBPlayerContainer, { playerHidden: !vm.playerVisible, playButtonVisible: playButtonVisible, onClickPlayButton: onClick },
        React.createElement(LBPlayer, { getVideoRef: (ref) => setVideoEl(ref), getPlayer: (player) => setLbPlayerCtx(player), videoOptions: lbVideoOptions(src), resultOptions: resultOptions, operationOptions: videoOperationProps })));
});
/*=============================================
= ViewModel =
=============================================*/
export class VideoVM {
    get playerStatus() {
        return this.ctx.status;
    }
    get resultSectionVisible() {
        return this.playerStatus === VideoPlayerStatus.Finished;
    }
    get playerVisible() {
        return this.playStarted;
    }
    constructor(router, navigate, apiClient, lessonService, signedCookieService, isAutoPlay, onClose, lesson, program, playPrevVideo, playNextVideo, courseLessonID, beforeCompleteLesson, afterCompleteLesson, requestExitConfirm, hideAutoPlay = false) {
        Object.defineProperty(this, "apiClient", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "playStarted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "playButtonVisible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // 現在再生されているプログラムを保持
        Object.defineProperty(this, "playingProgram", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // 現在再生されているレッスンを保持
        Object.defineProperty(this, "playingLesson", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // 動画視聴完了後のUIのVM
        Object.defineProperty(this, "resultVM", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // 動画の再生状態のステータス
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: VideoState.Loading
        });
        Object.defineProperty(this, "isAutoPlayNextVideo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // true の場合は自動再生トグルを非表示にし、自動再生も常にOFFにする
        Object.defineProperty(this, "hideAutoPlay", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isFirstLesson", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isLastLesson", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "prevLesson", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "nextLesson", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "courseLessonID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // エンディング開始秒に到達した際に表示する「完了」ボタンのVM
        Object.defineProperty(this, "completeButtonVM", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new VjsCompleteButtonVM()
        });
        // finish リクエスト送信済みかどうか。エンディング到達時点で完了を確定させ、以降 finish / sync は送らない
        Object.defineProperty(this, "lessonCompleteRequested", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // エンディング到達時に送信した finish の初回レスポンス。結果画面への遷移時に再利用する
        Object.defineProperty(this, "completionResultPromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "isCompletionLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "router", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "navigate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lessonService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "signedCookieService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "ctx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new VideoPlayerContext()
        });
        Object.defineProperty(this, "videoElResolved", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "lbCtxResolved", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "lbPlayerCtx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Promise((resolve) => (this.lbCtxResolved = resolve))
        });
        Object.defineProperty(this, "videoEl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Promise((resolve) => (this.videoElResolved = resolve))
        });
        Object.defineProperty(this, "elEventHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "collector", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Subscription()
        });
        Object.defineProperty(this, "time", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "session", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "analyzer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new VideoPlayAnalyzer()
        });
        Object.defineProperty(this, "videoType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // 動画終了時にページに戻った場合のコールバック
        Object.defineProperty(this, "onClose", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "afterCompleteLesson", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "beforeCompleteLesson", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "playPrevVideo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "playNextVideo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // 戻るボタン押下時に親側で離脱確認を割り込ませたい場合に渡すフック。
        // true を返すと VideoVM 側の標準離脱フロー（onLessonQuit 以降）は実行されない。
        Object.defineProperty(this, "requestExitConfirm", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        makeObservable(this, {
            playStarted: observable,
            isAutoPlayNextVideo: observable,
            playingProgram: observable.ref,
            playingLesson: observable.ref,
            resultVM: observable,
            state: observable,
            isCompletionLoading: observable,
            playButtonVisible: observable,
            playerStatus: computed,
            resultSectionVisible: computed,
            playerVisible: computed,
        });
        this.router = router;
        this.navigate = navigate;
        this.apiClient = apiClient;
        this.lessonService = lessonService;
        this.signedCookieService = signedCookieService;
        this.elEventHandler = new VideoElementEventHandler(this, this.ctx, this.analyzer);
        this.hideAutoPlay = hideAutoPlay;
        this.setIsAutoPlayNextVideo();
        this.playPrevVideo = playPrevVideo;
        this.playNextVideo = playNextVideo;
        this.onClose = onClose;
        this.beforeCompleteLesson = beforeCompleteLesson;
        this.afterCompleteLesson = afterCompleteLesson;
        this.requestExitConfirm = requestExitConfirm;
        // 動画視聴完了後のUIのVMの初期設定
        this.initializeResultVM();
        // イベントの監視設定
        this.bindEvents();
        // VideoVMの初期処理完了
        this.onLoadComplete();
        if (isAutoPlay) {
            this.playLessonVideo(program, lesson, courseLessonID);
        }
    }
    // video要素の初期化処理（video要素のrefを取得してから実行される必要がある）
    initializeVideoElement(videoEl, lbPlayerCtx) {
        this.videoElResolved(videoEl);
        this.lbCtxResolved(lbPlayerCtx);
        this.elEventHandler.initialize(videoEl);
    }
    // レッスン動画を動画プレイヤーに設定する
    setLessonVideo(program, lesson, courseLessonID) {
        return __awaiter(this, void 0, void 0, function* () {
            // 再生する動画を設定
            yield this.setVideo(lesson.video);
            this.setPlayingProgram(program);
            this.setPlayingLesson(lesson);
            this.setCourseLessonID(courseLessonID);
            this.videoType = VideoType.Lesson;
        });
    }
    // レッスン動画を再生する
    playLessonVideo(program, lesson, courseLessonID) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.signedCookieService.fetchSignedCookies(); // 動画を閲覧するための署名付きCookie取得
            yield this.setLessonVideo(program, lesson, courseLessonID).then(() => this.playWithTimeout());
        });
    }
    // 前のレッスン動画を設定する
    setPrevLesson(lesson) {
        this.prevLesson = lesson;
    }
    // 次のレッスン動画を設定する
    setNextLesson(lesson) {
        this.nextLesson = lesson;
        this.resultVM.setNextLesson(lesson);
    }
    // 自動再生を設定する
    setIsAutoPlayNextVideo() {
        return __awaiter(this, void 0, void 0, function* () {
            // 自動再生無効時は、ユーザー設定に関わらず自動再生を常にOFFにする
            if (this.hideAutoPlay) {
                this.isAutoPlayNextVideo = false;
                return;
            }
            const profile = yield this.apiClient.fetchMypageProfile();
            this.isAutoPlayNextVideo = profile.isAutoPlayNextVideo;
        });
    }
    // プログラムの紹介動画を再生する
    playProgramIntroduceVideo(program) {
        return __awaiter(this, void 0, void 0, function* () {
            // 再生する動画を設定
            yield this.setVideo(program.video);
            this.videoType = VideoType.ProgramIntroduce;
            this.playWithTimeout();
        });
    }
    // 再生ボタン押下時のハンドリング
    onClickPlayButton() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.signedCookieService.fetchSignedCookies();
        });
    }
    // 動画プレイヤー内の戻るボタン押下時のハンドリング
    onClickExitInnerFullScreen() {
        return __awaiter(this, void 0, void 0, function* () {
            // 親側で離脱確認ダイアログを挟みたい場合は、動画を pause / exitFullScreen のみ行って中断。
            // コントローラには pause 状態（play.svg）を表示しておき、ユーザが「やっぱり続ける」後に再生再開できるようにする。
            if (this.requestExitConfirm && this.requestExitConfirm()) {
                this.pause();
                this.exitFullScreen();
                yield this.showPausedController();
                return;
            }
            this.pause();
            this.exitFullScreen();
            this.playStarted = false;
            yield this.onLessonQuit();
            // resultVMの後処理
            this.resultVM.finishLessonResultSection();
            this.unbindEvents();
            if (this.onClose) {
                this.onClose();
            }
            this.navigate(-1);
        });
    }
    // 動画プレイヤー内の前へボタン押下時のハンドリング
    onClickPrev() {
        return __awaiter(this, void 0, void 0, function* () {
            this.pause();
            this.exitFullScreen();
            this.playStarted = false;
            yield this.onLessonQuit();
            // resultVMの後処理
            this.resultVM.finishLessonResultSection();
            this.unbindEvents();
            if (this.onClose) {
                this.onClose();
            }
            this.playPrevVideo();
        });
    }
    // 動画プレイヤー内の次へボタン押下時のハンドリング
    onClickNext() {
        return __awaiter(this, void 0, void 0, function* () {
            this.pause();
            this.exitFullScreen();
            this.playStarted = false;
            // resultVMの後処理
            this.resultVM.finishLessonResultSection();
            this.unbindEvents();
            if (this.onClose) {
                this.onClose();
            }
            this.playNextVideo();
        });
    }
    // 動画の視聴完了時のハンドリング
    onLessonComplete() {
        var _a, _b;
        if (this.isProgramIntroduceVideo()) {
            return;
        }
        // エンディング中の完了ボタン押下で結果画面へ遷移済みの場合、ended イベント等での二重実行を防ぐ
        if (this.state === VideoState.Result) {
            return;
        }
        const isChallengeProgram = (_a = this.playingProgram) === null || _a === void 0 ? void 0 : _a.isChallenge;
        const orderIndex = (_b = this.playingLesson) === null || _b === void 0 ? void 0 : _b.orderIndex;
        // 結果画面（次の動画カウントダウン等）では完了ボタンを表示しない
        this.completeButtonVM.visible = false;
        if (this.beforeCompleteLesson)
            this.beforeCompleteLesson();
        if (!this.session) {
            return;
        }
        // エンディング到達時に finish 送信済みの場合は再リクエストせず、保持済みの初回レスポンスを使う
        if (!this.completionResultPromise) {
            this.lessonCompleteRequested = true;
            this.completionResultPromise = this.session.complete(false, orderIndex, isChallengeProgram, this.playingProgram);
        }
        const completionPromise = this.completionResultPromise;
        this.resetAnalyzer();
        this.state = VideoState.Result;
        // 完了ボタン押下時はエンディング再生中のため、動画を終了位置まで進めて自然終了と同じ状態にする。
        // シーク完了前に動画が非表示になると最終フレームが描画されないため、シーク完了を待ってから結果画面へ進む
        this.seekToEnd().then(() => {
            this.hideController();
            this.startResult();
        });
        const onCompleted = (lessonCompletionResult) => {
            if (this.afterCompleteLesson)
                this.afterCompleteLesson(lessonCompletionResult);
        };
        if (this.courseLessonID && this.resultVM) {
            // コース/デイリーは完了アニメーション終了後に、完了API応答待ちのローディングを表示してから遷移する
            this.resultVM.setAfterAnimationFinished(() => {
                this.setCompletionLoading(true);
                completionPromise.then(onCompleted).finally(() => this.setCompletionLoading(false));
            });
            return;
        }
        completionPromise.then(onCompleted);
    }
    setCompletionLoading(isLoading) {
        this.isCompletionLoading = isLoading;
    }
    // レッスン動画の一時停止時のハンドリング
    onLessonSuspend() {
        if (this.isProgramIntroduceVideo()) {
            return;
        }
        if (this.state !== VideoState.Playing) {
            return;
        }
        if (this.session) {
            this.session.susppend(this.time, this.playingProgram);
        }
    }
    // フルスクリーン解除時のハンドリング
    onFullscreenCancellation() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isProgramIntroduceVideo()) {
                return;
            }
            const player = yield this.lbPlayerCtx;
            if (player.isDisposed())
                return null;
            const VjsVideoController = player.getChild('VjsVideoController');
            VjsVideoController.setIsPaused(true);
        });
    }
    // レッスン動画のシーク時のハンドリング
    onLessonSeeked(currentTime) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isProgramIntroduceVideo()) {
                return;
            }
            // シーク（飛ばし見）でのエンディング到達も完了とみなす（一時停止中のシークは timeupdate が発火しないためここで判定する）
            this.handleEndingReached(currentTime);
            const player = yield this.lbPlayerCtx;
            if (player.isDisposed())
                return null;
            const VjsVideoController = player.getChild('VjsVideoController');
            VjsVideoController.setHideTimer();
        });
    }
    // 再生中のプログラムを設定する
    setPlayingProgram(playingProgram) {
        return __awaiter(this, void 0, void 0, function* () {
            this.playingProgram = playingProgram;
            const player = yield this.lbPlayerCtx;
            const VjsVideoSideSuper = player.getChild('VjsVideoSideSuper');
            VjsVideoSideSuper.vm.setSeriesTitle(playingProgram.name);
        });
    }
    // 再生中のレッスンを設定する
    setPlayingLesson(playingLesson) {
        return __awaiter(this, void 0, void 0, function* () {
            this.playingLesson = playingLesson;
            // レッスンが切り替わったら完了状態をリセットする（再生位置に応じて再判定される）
            this.lessonCompleteRequested = false;
            this.completionResultPromise = null;
            this.completeButtonVM.visible = false;
            const player = yield this.lbPlayerCtx;
            const VjsVideoSideSuper = player.getChild('VjsVideoSideSuper');
            VjsVideoSideSuper.vm.setTitle(playingLesson.name);
        });
    }
    // 再生時間の更新時に、エンディング開始秒への到達判定を行う
    onTimeUpdate(currentTime) {
        this.handleEndingReached(currentTime);
    }
    // エンディング開始秒への到達判定。到達していれば完了ボタンを表示し、finish リクエストを一度だけ送る
    handleEndingReached(currentTime) {
        // レッスン動画のみ対象
        if (!this.isLessonVideo()) {
            this.completeButtonVM.visible = false;
            return;
        }
        // 結果画面（次の動画カウントダウン表示中）等では完了ボタンを表示しない
        if (this.state !== VideoState.Playing) {
            this.completeButtonVM.visible = false;
            return;
        }
        const endingStartSec = this.endingStartSec();
        const reached = endingStartSec != null && currentTime >= endingStartSec;
        this.completeButtonVM.visible = reached;
        if (reached) {
            this.requestFinishOnEndingReached();
        }
    }
    // エンディング開始秒を返す。
    // DBの実値（ending_start_sec）をそのまま使う。未設定（null）の場合はエンディングUIを表示しない。
    endingStartSec() {
        var _a, _b;
        return (_b = (_a = this.playingLesson) === null || _a === void 0 ? void 0 : _a.endingStartSec) !== null && _b !== void 0 ? _b : null;
    }
    // エンディング開始への到達時点で完了を確定させるため、finish リクエストを一度だけ送る。
    // カード表示中に離脱したユーザーも完了扱いにするための仕様。
    // 視聴統計はこの時点のスナップショットが送られるため、エンディング分はカロリー等に加算されない。
    requestFinishOnEndingReached() {
        var _a, _b;
        if (this.lessonCompleteRequested || !this.session) {
            return;
        }
        this.lessonCompleteRequested = true;
        this.completionResultPromise = this.session.complete(false, (_a = this.playingLesson) === null || _a === void 0 ? void 0 : _a.orderIndex, (_b = this.playingProgram) === null || _b === void 0 ? void 0 : _b.isChallenge, this.playingProgram);
    }
    // コース用のレッスンIDを設定する
    setCourseLessonID(courseLessonID) {
        return __awaiter(this, void 0, void 0, function* () {
            this.courseLessonID = courseLessonID;
        });
    }
    // day の最初のレッスンかどうかを設定する
    setIsFirstLesson(isFirstLesson) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isFirstLesson = isFirstLesson;
        });
    }
    // day の最後のレッスンかどうかを設定する
    setIsLastLesson(isLastLesson) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isLastLesson = isLastLesson;
        });
    }
    // 動画プレイヤーにクラスを追加する
    addClass(className) {
        return __awaiter(this, void 0, void 0, function* () {
            const videoEl = yield this.videoEl;
            videoEl.classList.add(className);
        });
    }
    // 動画プレイヤーのクラスを削除する
    removeClass(className) {
        return __awaiter(this, void 0, void 0, function* () {
            const videoEl = yield this.videoEl;
            videoEl.classList.remove(className);
        });
    }
    bindEvents() {
        // window の unload イベントを変更を監視
        this.router.outbound$
            .subscribe(() => {
            this.onLessonQuit();
        })
            .addTo(this.collector);
        // 画面遷移を監視
        this.router.locationChange$
            .pipe(take(1))
            .subscribe(() => {
            this.onLessonQuit();
            this.unbindEvents();
        })
            .addTo(this.collector);
    }
    // 動画視聴完了後のUIのVMを初期設定する
    initializeResultVM() {
        return __awaiter(this, void 0, void 0, function* () {
            this.resultVM = new LessonResultSectionVM(this.playNextVideo);
            // 自動再生無効時は、視聴後の自動遷移も常にOFFにする（初期値が true のため明示的に落とす）
            if (this.hideAutoPlay) {
                this.resultVM.setIsAutoPlay(false);
            }
            yield this.resultVM.initialize(this);
        });
    }
    // 動画のコントローラーを表示する
    showController() {
        return __awaiter(this, void 0, void 0, function* () {
            const player = yield this.lbPlayerCtx;
            const VjsVideoController = player.getChild('VjsVideoController');
            VjsVideoController.show();
        });
    }
    // 動画のコントローラーを非表示にする
    hideController() {
        return __awaiter(this, void 0, void 0, function* () {
            const player = yield this.lbPlayerCtx;
            const VjsVideoController = player.getChild('VjsVideoController');
            VjsVideoController.hide();
        });
    }
    onLoadComplete() {
        this.state = VideoState.Ready;
    }
    onProgramIntroduceStart() {
        if (!this.playingProgram) {
            return;
        }
        this.state = VideoState.Playing;
    }
    isProgramIntroduceVideo() {
        return this.videoType === VideoType.ProgramIntroduce;
    }
    isLessonVideo() {
        return this.videoType === VideoType.Lesson;
    }
    setIsPaused(isPaused) {
        return __awaiter(this, void 0, void 0, function* () {
            const player = yield this.lbPlayerCtx;
            const VjsVideoController = player.getChild('VjsVideoController');
            VjsVideoController.setIsPaused(isPaused);
        });
    }
    // 離脱確認ダイアログ表示時に、動画プレイヤー上のコントローラに pause 状態（play.svg）を表示する。
    showPausedController() {
        return __awaiter(this, void 0, void 0, function* () {
            const player = yield this.lbPlayerCtx;
            if (player.isDisposed())
                return;
            const VjsVideoController = player.getChild('VjsVideoController');
            VjsVideoController.setIsPaused(true);
        });
    }
    onLessonStart() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.playingLesson) {
                return;
            }
            this.session = yield this.lessonService.startLesson(this.playingLesson, this.playingProgram, this.courseLessonID);
            this.session.setVideoPlayAnalalyzor(this.analyzer);
            this.state = VideoState.Playing;
        });
    }
    // 動画が途中で止められた場合のハンドリング
    onLessonQuit() {
        var _a;
        if (this.state !== VideoState.Playing) {
            return Promise.resolve();
        }
        // エンディング到達で finish 送信済みの場合、quit による finish リクエストは送らない（完了は確定済み）
        if (this.lessonCompleteRequested) {
            return Promise.resolve();
        }
        if (this.session) {
            // TODO: isAutoPlayフラグのBQへのパラメータ設定
            // this.session.quit(this.time, this.isAutoPlay, this.playingLesson.orderIndex).then(result => {
            return this.session.quit(this.time, false, this.playingLesson.orderIndex, this.playingProgram, (_a = this.playingProgram) === null || _a === void 0 ? void 0 : _a.isChallenge);
        }
        this.resultVM = null;
        this.state = VideoState.Quit;
        return Promise.resolve();
    }
    onReady() {
        return __awaiter(this, void 0, void 0, function* () {
            this.ctx.setReady();
        });
    }
    startResult() {
        if (!this.resultVM) {
            return;
        }
        if (this.state !== VideoState.Result) {
            return;
        }
        this.resultVM.start();
    }
    unbindEvents() {
        this.collector.unsubscribe();
    }
    setVideo(video) {
        return __awaiter(this, void 0, void 0, function* () {
            this.ctx.reset();
            const player = yield this.lbPlayerCtx;
            player.src({
                src: video.hlsURL,
                withCredentials: true,
            });
            this.onReady();
        });
    }
    // iPhoneでの再生の際、Timeoutを設定しないと再生されないため、setTimeoutを設定。
    //（300millisecに特に意味はないため再生できれば、どのような値でも問題ない）
    playWithTimeout() {
        setTimeout(() => this.play(), 300);
    }
    // 動画を再生する
    play() {
        return __awaiter(this, void 0, void 0, function* () {
            const player = yield this.lbPlayerCtx;
            player
                .play()
                .then(() => {
                this.playStarted = true;
                this.playButtonVisible = false;
            })
                .catch((e) => {
                // LBでは自動再生を実行しているので、再生がエラーになった場合、動画のサムネイルを表示する
                this.playStarted = false;
                this.playButtonVisible = true;
                throw e;
            });
            // TODO: いずれの条件にも一致しない場合は例外を投げて上位でハンドリングする設計にする
            if (this.isProgramIntroduceVideo()) {
                this.onProgramIntroduceStart();
            }
            else if (this.isLessonVideo()) {
                this.onLessonStart().then(() => {
                    // 1分おきにサーバへ視聴アクティビティの同期を行う
                    // clearInterval処理は動画終了したタイミングで自動で行われる
                    player.setInterval(() => {
                        // finish 送信済みの場合は sync も送らない（エンディング分の視聴は集計しない）
                        if (!player.paused() && !this.lessonCompleteRequested)
                            this.session.sync();
                    }, 60000);
                });
            }
            const onLoadedMetadata = () => {
                this.analyzer.setDuration(player.duration());
            };
            // Videojsでloadedmetadataイベントが発行されるまで動画の長さを取得できないので、loadedmetadataイベントが発行されるのを待つ
            if (player.readyState() < 1) {
                // wait for loadedmetdata event
                player.one('loadedmetadata', onLoadedMetadata);
            }
            else {
                onLoadedMetadata();
            }
        });
    }
    pause() {
        return __awaiter(this, void 0, void 0, function* () {
            const player = yield this.lbPlayerCtx;
            player.pause();
        });
    }
    // 動画を終了位置まで進める（完了ボタン押下時に自然終了と同じ状態にするため）
    // video要素へ直接シークして動画自体を末尾へ移動する。
    // seeked イベントが発火しないケースでも処理が止まらないよう、フォールバックのタイムアウト付きで完了を待つ
    seekToEnd() {
        return __awaiter(this, void 0, void 0, function* () {
            const player = yield this.lbPlayerCtx;
            const videoEl = yield this.videoEl;
            if (player.ended()) {
                return;
            }
            return new Promise((resolve) => {
                let resolved = false;
                const onSeeked = () => {
                    if (resolved) {
                        return;
                    }
                    resolved = true;
                    videoEl.removeEventListener('seeked', onSeeked);
                    resolve();
                };
                videoEl.addEventListener('seeked', onSeeked);
                window.setTimeout(onSeeked, 1000);
                if (isFinite(videoEl.duration)) {
                    // duration ちょうどは終端フレームのデータが無くシークが完了しない（映像が更新されない）ことがあるため、
                    // わずかに手前へシークする。再生中であれば残りを再生しきって自然終了（ended）に到達する
                    videoEl.currentTime = Math.max(0, videoEl.duration - 0.1);
                }
                else {
                    onSeeked();
                }
            });
        });
    }
    // フルスクリーンを解除する
    exitFullScreen() {
        return __awaiter(this, void 0, void 0, function* () {
            if (document.exitFullscreen) {
                try {
                    yield document.exitFullscreen();
                }
                catch (_a) {
                    // フルスクリーン状態じゃない時にexitFullscreen()すると落ちるので意図的にスルーする
                    // https://github.com/jpilfold/ngx-image-viewer/issues/23#issuecomment-444614674
                }
            }
            else if (document.webkitCancelFullScreen) {
                yield document.webkitCancelFullScreen();
            }
            else if (this.videoEl.msExitFullscreen) {
                const castedValue = this.videoEl; // 自動補完でセミコロンが挿入される不具合？回避のための代入
                castedValue.msExitFullscreen();
            }
        });
    }
    resetAnalyzer() {
        this.analyzer = new VideoPlayAnalyzer();
        this.elEventHandler.setAnalyzer(this.analyzer);
    }
}
__decorate([
    bind
], VideoVM.prototype, "setLessonVideo", null);
__decorate([
    bind
], VideoVM.prototype, "playLessonVideo", null);
__decorate([
    bind
], VideoVM.prototype, "setPrevLesson", null);
__decorate([
    bind
], VideoVM.prototype, "setNextLesson", null);
__decorate([
    bind
], VideoVM.prototype, "setIsAutoPlayNextVideo", null);
__decorate([
    bind
], VideoVM.prototype, "playProgramIntroduceVideo", null);
__decorate([
    bind
], VideoVM.prototype, "onClickPlayButton", null);
__decorate([
    bind
], VideoVM.prototype, "onClickExitInnerFullScreen", null);
__decorate([
    bind
], VideoVM.prototype, "onClickPrev", null);
__decorate([
    bind
], VideoVM.prototype, "onClickNext", null);
__decorate([
    bind
], VideoVM.prototype, "onLessonComplete", null);
__decorate([
    bind
], VideoVM.prototype, "setCompletionLoading", null);
__decorate([
    bind
], VideoVM.prototype, "onLessonSuspend", null);
__decorate([
    bind
], VideoVM.prototype, "onFullscreenCancellation", null);
__decorate([
    bind
], VideoVM.prototype, "onLessonSeeked", null);
__decorate([
    bind
], VideoVM.prototype, "setPlayingProgram", null);
__decorate([
    bind
], VideoVM.prototype, "setPlayingLesson", null);
__decorate([
    bind
], VideoVM.prototype, "onTimeUpdate", null);
__decorate([
    bind
], VideoVM.prototype, "setCourseLessonID", null);
__decorate([
    bind
], VideoVM.prototype, "setIsFirstLesson", null);
__decorate([
    bind
], VideoVM.prototype, "setIsLastLesson", null);
__decorate([
    bind
], VideoVM.prototype, "addClass", null);
__decorate([
    bind
], VideoVM.prototype, "removeClass", null);
__decorate([
    bind
], VideoVM.prototype, "setIsPaused", null);
__decorate([
    bind
], VideoVM.prototype, "showPausedController", null);
__decorate([
    bind
], VideoVM.prototype, "setVideo", null);
__decorate([
    bind
], VideoVM.prototype, "playWithTimeout", null);
__decorate([
    bind
], VideoVM.prototype, "play", null);
